import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useRoleQuery } from '../hooks/useRoleQuery';
import { RoleFlags } from '../components/RoleFlags';
import { RoleIdentityPanel } from '../components/RoleIdentityPanel';
import { RolePermissionsPanel } from '../components/RolePermissionsPanel';
import { RoleUsersPanel } from '../components/RoleUsersPanel';
import { groupPermissionsByResource } from '../utils/groupPermissionsByResource';
import { Alert, TextLink } from '@/shared/components';

export function RoleDetail() {
  const { id } = useParams<{ id: string }>();
  const roleQuery = useRoleQuery(id ?? '');

  const groups = useMemo(
    () => groupPermissionsByResource(roleQuery.data?.permissions ?? []),
    [roleQuery.data?.permissions],
  );

  if (!id) {
    return <p className="text-mp-error">Invalid role ID.</p>;
  }

  if (roleQuery.isLoading) {
    return (
      <p role="status" aria-live="polite">
        Loading role...
      </p>
    );
  }

  if (roleQuery.isError || !roleQuery.data) {
    return (
      <Alert variant="error">
        <span>Role not found or failed to load.</span>
      </Alert>
    );
  }

  const role = roleQuery.data;

  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2">
        <TextLink to="/admin/roles" color="secondary" className="text-sm">
          &larr; Back to Roles
        </TextLink>
      </div>

      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-semibold">{role.display_name ?? role.name}</h1>
        <RoleFlags is_elevated={role.is_elevated} is_internal={role.is_internal} />
      </div>

      <Alert variant="info" role="note">
        Roles are managed by the platform. Roles and their permissions are defined in the
        rbac-service seeders and cannot be edited here.
      </Alert>

      <RoleIdentityPanel role={role} />
      <RolePermissionsPanel groups={groups} total={role.permissions.length} />
      <RoleUsersPanel key={id} roleId={id} />
    </section>
  );
}
